import { useCallback, useState } from 'react';

import { MAIL_PATH } from '@/utils/constants';

type MutateCallbacks = {
  onSuccess?: () => void;
  onError?: () => void;
};

// Plain-fetch replacement for the former react-query useMutation wrapper, keeping the same
// { mutate, isLoading } surface its consumers use. Contact forms render on many static pages,
// and this hook was the only thing forcing the react-query runtime onto them after the
// provider moved out of _app (see PERFORMANCE-RECOMMENDATIONS.md § Etap 4.2).
// One deliberate behavior change vs useMutation: a non-2xx response now calls onError
// (previously any completed request counted as success, so a failing mail API still
// redirected users to /thanks).
export const useSendEmail = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback(async (formData: FormData, { onSuccess, onError }: MutateCallbacks = {}) => {
    setIsLoading(true);
    try {
      const response = await fetch(MAIL_PATH, { method: 'POST', body: formData });
      if (!response.ok) {
        throw new Error(`Mail request failed with status ${response.status}`);
      }
      onSuccess?.();
    } catch {
      onError?.();
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading };
};
